'use client';

import React from 'react';
import NavBar from '@/components/header/NavBar';
import Why from '@/components/whySection';
import ContactForm from '@/components/ContactForm/ContactForm';
import Footer from '@/components/Footer/Footer';
import Image from "next/image";
import { FaAngleDoubleRight } from "react-icons/fa";

const features = [
  {
    title: "Advanced E-Commerce Engine",
    description:
      "A full online store built for padel gear — catalog, variants, stock, and checkout, engineered to handle real product complexity without slowing down.",
  },
  {
    title: "10-Language Storefront",
    description:
      "The entire shop, from product pages to checkout and emails, is fully localized across 10 languages, letting Padlev sell confidently across every market it reaches.",
  },
  {
    title: "BTCPay Crypto Checkout",
    description:
      "A self-hosted BTCPay Server integration lets customers pay in Bitcoin and other cryptocurrencies, alongside traditional payment methods, with no third-party custody of funds.",
  },
  {
    title: "Loyalty Points System",
    description:
      "Every purchase earns customers points they can redeem on future orders, custom-built to reward repeat buyers and keep them coming back.",
  },
  {
    title: "Coupon Code Engine",
    description:
      "Flexible, rule-based coupon codes support percentage or fixed discounts, expiry windows, and usage limits, giving Padlev full control over promotions.",
  },
  {
    title: "Automated Email Marketing",
    description:
      "Celery-powered background jobs handle abandoned cart reminders, order updates, and campaign sends, keeping customers engaged without manual work.",
  },
];

const gallery = [
  {
    src: "/padlev/3.avif",
    title: "Storefront & Catalog",
    content:
      "A clean, fast-loading product catalog with rich filtering, built so customers can find the right padel racket or gear in seconds, in their own language.",
  },
  {
    src: "/padlev/2.avif",
    title: "Checkout & Payments",
    content:
      "A streamlined checkout supporting cards and crypto side by side, with coupon and points redemption built directly into the flow.",
  },
  {
    src: "/padlev/3.avif",
    title: "Customer Dashboard",
    content:
      "Customers track orders, redeem loyalty points, and manage their account from a dashboard designed to keep them coming back.",
  },
];

const stack = {
  Frontend: ["TypeScript", "Next.js", "Tailwind CSS"],
  Backend: ["Django", "Celery", "PostgreSQL"],
};

const ContactForms: React.FC = () => {
  return (
    <div className="rounded-b-3xl">
      {/* Hero */}
      <div
        className="bg-cover bg-center bg-no-repeat h-96 lg:h-[600px] relative rounded-b-3xl mb-2"
        style={{ backgroundImage: `url('/hero/padel.avif')` }}
      >
        <div className="absolute inset-0 bg-secondary/55 h-96 lg:h-[600px] rounded-b-3xl" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 gap-4 lg:gap-6">
          <p className="text-white/70 text-sm lg:text-base tracking-wide">
            E-Commerce · Web Platform
          </p>
          <h1 className="text-white text-5xl lg:text-9xl font-extrabold">
            Padlev
          </h1>
          <p className="text-white/80 text-lg lg:text-2xl max-w-2xl">
            A multilingual padel e-commerce platform built for global reach,
            modern payments, and customer loyalty.
          </p>
          <a
            href="https://www.padlev.com/fr"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 lg:mt-4 inline-flex items-center gap-2 bg-white text-primary font-semibold px-6 py-3 rounded-full text-sm lg:text-base hover:bg-white/90 transition-colors"
          >
            Visit padlev.com <FaAngleDoubleRight size={14} />
          </a>
        </div>
      </div>

      <NavBar />

      {/* Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 bg-background">
        <div className="relative w-full h-72 lg:h-auto">
          <Image
            src="/padlev/1.avif"
            alt="Padlev storefront overview"
            fill
            sizes="(max-width: 1024px) 100vw, 50vw"
            className="object-cover"
          />
        </div>
        <div className="space-y-6 lg:space-y-8 p-6 md:p-12 lg:p-24 flex flex-col justify-center">
          <h2 className="text-3xl lg:text-5xl font-extrabold text-primary">
            Project Overview
          </h2>
          <p className="max-w-prose text-base lg:text-xl leading-relaxed text-neutral-600">
            Padlev needed more than a standard online store. As a padel
            equipment retailer selling across multiple countries, they needed
            a platform that could speak their customers' languages, accept
            the payment methods those customers actually wanted to use, and
            reward loyalty without relying on a patchwork of third-party
            plugins.
          </p>
          <p className="max-w-prose text-base lg:text-xl leading-relaxed text-neutral-600">
            We built Padlev from the ground up on Next.js and TypeScript for
            a fast, reliable frontend, backed by a Django and Celery
            architecture handling everything from order processing to
            automated email marketing — giving Padlev a store that scales
            with their ambitions.
          </p>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-primary py-12 lg:py-20 px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4 text-center">
          <div>
            <p className="text-4xl lg:text-6xl font-extrabold text-white">10</p>
            <p className="text-white/70 text-sm lg:text-base mt-2">Languages supported</p>
          </div>
          <div>
            <p className="text-4xl lg:text-6xl font-extrabold text-white">₿</p>
            <p className="text-white/70 text-sm lg:text-base mt-2">Crypto payments via BTCPay</p>
          </div>
          <div>
            <p className="text-4xl lg:text-6xl font-extrabold text-white">★</p>
            <p className="text-white/70 text-sm lg:text-base mt-2">Loyalty points on every order</p>
          </div>
          <div>
            <p className="text-4xl lg:text-6xl font-extrabold text-white">%</p>
            <p className="text-white/70 text-sm lg:text-base mt-2">Custom coupon engine</p>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="bg-background p-6 md:p-12 lg:p-24">
        <h2 className="text-3xl lg:text-6xl font-extrabold text-primary mb-8 lg:mb-16">
          What We Built
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
          {features.map((feature, i) => (
            <div
              key={i}
              className="px-5 py-5 lg:px-6 lg:py-6 border border-dashed border-secondary rounded-2xl"
            >
              <h3 className="text-lg lg:text-xl font-bold mb-2">
                {feature.title}
              </h3>
              <p className="text-sm lg:text-base text-neutral-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Gallery */}
      <div className="bg-secondary text-white p-6 md:p-12 lg:p-24">
        <h2 className="text-3xl lg:text-6xl font-extrabold mb-8 lg:mb-16">
          A Closer Look
        </h2>
        <div className="space-y-12 lg:space-y-20">
          {gallery.map((item, i) => {
            const isReversed = i % 2 === 1;
            return (
              <div
                key={i}
                className={`flex flex-col lg:gap-16 items-center ${
                  isReversed ? "lg:flex-row-reverse" : "lg:flex-row"
                }`}
              >
                <div className="relative w-full h-64 sm:h-96 lg:h-[500px] rounded-3xl overflow-hidden shadow-lg">
                  <Image
                    src={item.src}
                    alt={item.title}
                    fill
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    className="object-cover rounded-3xl"
                  />
                </div>
                <div className="w-full space-y-3 lg:space-y-6 mt-6 lg:mt-0">
                  <h4 className="text-2xl lg:text-4xl font-bold">
                    {item.title}
                  </h4>
                  <p className="text-base lg:text-xl leading-relaxed text-neutral-600">
                    {item.content}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tech stack */}
      <div className="bg-primary p-6 md:p-12 lg:p-24 text-white">
        <h2 className="text-3xl lg:text-6xl font-extrabold mb-8 lg:mb-16">
          Tech Stack
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {Object.entries(stack).map(([group, items]) => (
            <div key={group}>
              <p className="text-white/60 text-sm lg:text-base mb-4">{group}</p>
              <div className="flex flex-wrap gap-2 lg:gap-3">
                {items.map((item) => (
                  <span
                    key={item}
                    className="px-4 py-2 rounded-full border border-white/30 text-sm lg:text-base"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <ContactForm />
      <Footer />
    </div>
  );
};

export default ContactForms;